import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Form,
  Row,
  Col,
  Button,
  Badge,
  Card,
  Table,
} from "react-bootstrap";

import {
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import { leadsAPI } from "../services/api";
import { useQuery } from "@tanstack/react-query";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/LeadManagement.css";

const PAGE_SIZE = 50;






const QualifiedLeadsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [page, setPage] = useState(1);

  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ------------------------------
  // Resize handling
  // ------------------------------
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // ------------------------------
  // Fetch data with React Query
  // ------------------------------
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["qualifiedLeads", page, statusFilter],
    queryFn: async () => {
      const res = await leadsAPI.getQualifiedLeads({
        page,
        page_size: PAGE_SIZE,
        status: statusFilter,
      });
      return {
        results: res.data.results?.results || [],
        count: res.data.count || 0,
      };
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const totalCount = data?.count || 0;

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // ------------------------------
  // Filtering
  // ------------------------------
  const filtered = useMemo(() => {
    const callsData = data?.results || [];
    const q = searchTerm.toLowerCase();
    return callsData.filter((c) => {
      const lead = c.lead || {};
      return (
        lead.name?.toLowerCase().includes(q) ||
        lead.phone?.includes(searchTerm) ||
        (lead.email || "").toLowerCase().includes(q) ||
        (c.outcome || "").toLowerCase().includes(q)
      );
    });
  }, [data?.results, searchTerm]);

  const formatDate = (s) =>
    s ? new Date(s).toLocaleDateString() : "Never";

  const badgeVariant = (outcome) => {
    const s = (outcome || "").toLowerCase();
    if (s.includes("immediate")) return "success";
    if (s.includes("hold")) return "warning";
    if (s.includes("cancel")) return "danger";
    if (s.includes("later")) return "info";
    if (s.includes("proposal")) return "primary";
    return "secondary";
  };

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  if (isLoading) return <LoadingSpinner text="Loading leads..." />;

  if (isError)
    return (
      <div className="text-center mt-5">
        <p className="text-danger">Error loading leads</p>
        <Button onClick={refetch}>Retry</Button>
      </div>
    );

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
 <Container fluid className="page-container">

      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
      <div className="mb-4">
  <h1 className="page-header-title">Proposal Status Leads {totalCount ? `(${totalCount})` : ""}</h1>
  <div className="page-header-subtitle">
    View contacted leads with proposal outcomes. Click rows to expand for call details, transcripts, and summaries.
  </div>
</div>


        {/* SEARCH */}
     <Form className="d-flex align-items-center" style={{ maxWidth: 350 }}>
  <Form.Control
    className="search-input"
    placeholder="Search name, phone, email…"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
  />
  <FaSearch className="text-muted ms-2" />
</Form>

      </div>

      {/* FILTER BUTTONS */}
      <div className="mb-4">
        {[
          { label: "All", value: "all" },
          { label: "Immediate Follow-Up", value: "immediate follow-up" },
          { label: "On Hold", value: "on hold" },
          { label: "Cancelled", value: "cancelled" },
          { label: "Call Later", value: "call later" },
          { label: "Ready for Proposal", value: "ready for proposal" },
        ].map((s) => (
         <Button
  key={s.value}
  variant={statusFilter === s.value ? "primary" : "outline-secondary"}
  className="filter-chip me-2 mb-2"
  onClick={() => { setStatusFilter(s.value); setPage(1); }}
>
  {s.label}
</Button>

        ))}
      </div>

{/* =======================
    MOBILE VIEW (CARDS)
======================= */}
{isMobile ? (
  <div className="mobile-lead-list">
    {filtered.length === 0 && (
      <div className="text-center py-4 text-muted">
        <FaFilter className="mb-2" size={22} />
        <div>No leads found</div>
      </div>
    )}

    {filtered.map((c) => {
      const lead = c.lead || {};
      const expanded = expandedId === c.id;

      return (
        <Card
          key={c.id}
          className="mb-3 mobile-lead-card"
          onClick={() => toggleExpand(c.id)}
        >
          <Card.Body>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="fw-semibold">{lead.name}</div>
                <small className="text-muted">{lead.phone}</small>
              </div>

              <Badge bg={badgeVariant(c.outcome)}>
                {c.outcome || "Unknown"}
              </Badge>
            </div>

            {/* Expanded content */}
            {expanded && (
              <div className="mt-3">
                <div className="mb-2">
                  <strong>Email:</strong> {lead.email || "—"}
                </div>

                <div className="mb-2">
                  <strong>Status:</strong> {lead.status || "—"}
                </div>

                <div className="mb-2">
                  <strong>Address:</strong> {lead.address || "—"}
                </div>

                <div className="mb-2">
                  <strong>Last Contacted:</strong>{" "}
                  {formatDate(lead.last_contacted)}
                </div>

                <hr />

                <div>
                  <strong>Summary:</strong>
                  <div className="mt-1">
                    {c.summary || "No summary available."}
                  </div>
                </div>
              </div>
            )}

            {/* Chevron */}
            <div className="text-center mt-2 text-muted">
              {expanded ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </Card.Body>
        </Card>
      );
    })}
  </div>
) : (
        <Table hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Outcome</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Last Contacted</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  <FaFilter className="mb-2" size={22} />
                  <div>No leads found</div>
                </td>
              </tr>
            )}

            {filtered.map((c) => {
              const lead = c.lead;

              return (
                <React.Fragment key={c.id}>
                  {/* MAIN ROW */}
                  <tr
                    className={`lead-row-clickable ${
                      expandedId === c.id ? "table-active" : ""
                    }`}
                    onClick={() => toggleExpand(c.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="lead-avatar">
                          {lead.name?.charAt(0)}
                        </div>
                        <div className="ms-2">
                          <div className="fw-semibold">{lead.name}</div>
                          {lead.email && (
                            <small className="text-muted">{lead.email}</small>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>
                      <Badge bg={badgeVariant(c.outcome)}>
                        {c.outcome || "Unknown"}
                      </Badge>
                    </td>

                    <td className="mono">{lead.phone}</td>

                    <td>
                      <Badge
                        bg={
                          lead.project_status === "proposal_sent"
                            ? "primary"
                            : lead.status?.toLowerCase().includes("follow")
                            ? "warning"
                            : "secondary"
                        }
                      >
                        {lead.status || "Not Set"}
                      </Badge>
                    </td>

                    <td>{formatDate(lead.last_contacted)}</td>

                    <td>
                    {expandedId === c.id 
  ? <FaChevronUp className="chevron-icon" /> 
  : <FaChevronDown className="chevron-icon" />}

                    </td>
                  </tr>

                  {/* EXPANDED ROW */}
                  {expandedId === c.id && (
                    <tr className="expanded-row">
                      <td colSpan={6}>
                        <Card className="shadow-sm my-2">
                          <Card.Body>
                            {/* Lead Info */}
                            <Row className="mb-3">
                              <Col md={6}>
                                <small className="text-muted">Address</small>
                                <div>{lead.address || "—"}</div>
                              </Col>

                              <Col md={3}>
                                <small className="text-muted">
                                  Proposed Price
                                </small>
                                <div>{lead.proposed_price || "—"}</div>
                              </Col>

                              <Col md={3}>
                                <small className="text-muted">
                                  Proposal Date
                                </small>
                                <div>
                                  {lead.proposal_date || "Not provided"}
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col md={6}>
                                <small className="text-muted">
                                  Project Description
                                </small>
                                <div>
                                  {lead.project_description ||
                                    "No project description."}
                                </div>
                              </Col>

                              <Col md={6}>
                                <small className="text-muted">
                                  Lead Context
                                </small>
                                <div>{lead.context || "—"}</div>
                              </Col>
                            </Row>

                            <hr />

                            {/* Call Info */}
                            <h6 className="fw-bold mb-3">Call Details</h6>

                            <Row className="mb-3">
                              <Col md={4}>
                                <small className="text-muted">Outcome</small>
                                <div>{c.outcome}</div>
                              </Col>

                              <Col md={4}>
                                <small className="text-muted">Started</small>
                                <div>{formatDate(c.started_at)}</div>
                              </Col>

                              <Col md={4}>
                                <small className="text-muted">Ended</small>
                                <div>{formatDate(c.ended_at)}</div>
                              </Col>
                            </Row>

                            <Row>
                              <Col md={12}>
                                <small className="text-muted">Summary</small>
                                <div>{c.summary || "No summary available."}</div>
                              </Col>
                            </Row>

                            {/* Transcript */}
                            {c.transcript && (
                              <details className="mt-3">
                                <summary className="fw-semibold text-primary">
                                  Show Transcript
                                </summary>
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    background: "#f8f9fa",
                                    padding: "1rem",
                                    borderRadius: "8px",
                                  }}
                                >
                                  {c.transcript}
                                </pre>
                              </details>
                            )}
                          </Card.Body>
                        </Card>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* PAGINATION */}
      <div className="d-flex justify-content-between mt-4">
        <Button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          ← Previous
        </Button>

        <span>Page {page}</span>

        <Button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next →
        </Button>
      </div>
    </Container>
  );
};

export default QualifiedLeadsPage;
